import { useId, useState, type FormEvent, type KeyboardEvent, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import ErrorBanner from "@/components/common/ErrorBanner";

/**
 * Dialog alba aplikace (založení, podalbum, přejmenování) místo nativního `prompt`
 * prohlížeče (F7.6). Vzhled `ConfirmModal`, přístupnost `DestinationCorrectionDialog`.
 *
 * Leží nad `AddToAlbumModal` (`z-50`), proto `z-[60]`. Klik do překryvu dialog nezavírá
 * a `Escape` zastaví šíření, ať nezavře nadřazený modal. Je to `<form>`, takže se nesmí
 * vykreslit uvnitř jiného formuláře (`AlbumTree manage` dnes jen na stránce Média
 * a v `AddToAlbumModal`, obojí mimo formulář).
 */
export type AlbumDialogTarget =
  | { mode: "create"; parentId: string | null; parentName: string | null }
  | { mode: "rename"; id: string; name: string };

export type AlbumNameError = "required";

/** Jméno alba je povinné (prázdné po ořezu); stejné pravidlo drží API (`album_name_required`). */
export function albumNameError(name: string): AlbumNameError | null {
  return name.trim() === "" ? "required" : null;
}

export type AlbumDialogRequest =
  | {
      method: "post";
      path: "/api/admin/media-folders";
      body: { name: string; parentId: string | null };
    }
  | { method: "patch"; path: string; body: { name: string } };

/** Požadavek, který dialog po potvrzení odešle; jméno ořezané. */
export function albumDialogRequest(target: AlbumDialogTarget, name: string): AlbumDialogRequest {
  const trimmed = name.trim();
  if (target.mode === "create") {
    return {
      method: "post",
      path: "/api/admin/media-folders",
      body: { name: trimmed, parentId: target.parentId },
    };
  }
  return {
    method: "patch",
    path: `/api/admin/media-folders/${encodeURIComponent(target.id)}`,
    body: { name: trimmed },
  };
}

export interface AlbumDialogViewProps {
  target: AlbumDialogTarget;
  name: string;
  fieldError: AlbumNameError | null;
  pending: boolean;
  error: Error | null;
  uid: string;
  onNameChange: (name: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}

export function AlbumDialogView({
  target,
  name,
  fieldError,
  pending,
  error,
  uid,
  onNameChange,
  onSubmit,
  onClose,
}: AlbumDialogViewProps): ReactElement {
  const { t } = useTranslation();
  const titleId = `${uid}-title`;
  const inputId = `${uid}-name`;
  const errorId = `${uid}-error`;
  const title =
    target.mode === "rename"
      ? t("mediaBulk.album.renameTitle")
      : target.parentId === null
        ? t("mediaBulk.album.createTitle")
        : t("mediaBulk.album.createChildTitle", { parent: target.parentName ?? "" });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSubmit();
  };
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-album-dialog=""
        noValidate
        onSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
        className="w-full max-w-md space-y-4 rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800"
      >
        <h3 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-white">
          {title}
        </h3>
        <div>
          <label htmlFor={inputId} className="label">
            {t("mediaBulk.album.nameLabel")}
          </label>
          <input
            id={inputId}
            name="name"
            type="text"
            autoFocus
            maxLength={120}
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            className={`input w-full ${fieldError ? "border-error-500" : ""}`}
            aria-invalid={fieldError ? true : undefined}
            aria-describedby={fieldError ? errorId : undefined}
          />
          {fieldError && (
            <p
              id={errorId}
              data-album-dialog-error=""
              className="text-error-600 dark:text-error-400 mt-1 text-sm"
            >
              {t("mediaBulk.album.nameRequired")}
            </p>
          )}
        </div>
        <ErrorBanner error={error} />
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-ghost" disabled={pending}>
            {t("common.actions.cancel")}
          </button>
          <button type="submit" className="btn-primary" disabled={pending}>
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("mediaBulk.album.save")}
          </button>
        </div>
      </form>
    </div>
  );
}

export interface AlbumDialogProps {
  target: AlbumDialogTarget;
  pending: boolean;
  error: Error | null;
  /** Ořezané, neprázdné jméno; prázdné dialog nepustí dál (chyba u pole). */
  onSubmit: (name: string) => void;
  onClose: () => void;
}

export default function AlbumDialog({
  target,
  pending,
  error,
  onSubmit,
  onClose,
}: AlbumDialogProps): ReactElement {
  const uid = useId();
  const [name, setName] = useState(target.mode === "rename" ? target.name : "");
  const [fieldError, setFieldError] = useState<AlbumNameError | null>(null);

  return (
    <AlbumDialogView
      target={target}
      name={name}
      fieldError={fieldError}
      pending={pending}
      error={error}
      uid={uid}
      onNameChange={(next) => {
        setName(next);
        if (fieldError && albumNameError(next) === null) setFieldError(null);
      }}
      onSubmit={() => {
        const problem = albumNameError(name);
        setFieldError(problem);
        if (problem === null) onSubmit(name.trim());
      }}
      onClose={onClose}
    />
  );
}
